import { useCallback, useEffect, useRef, useState } from 'react';
import { friendlyError } from '@/lib/errors';
import {
  downloadSedFile,
  loadSedFiles,
  loadSedRouteDetails,
  type SedDocument,
  type SedFileInfo,
  type SedRouteDetails,
} from '@/lib/sed';
import { useToastStore } from '@/store/toastStore';

export function useSedDocumentResources(doc: SedDocument | null) {
  const [route, setRoute] = useState<SedRouteDetails | null>(null);
  const [files, setFiles] = useState<SedFileInfo[]>([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [filesError, setFilesError] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const sequence = useRef(0);
  const docId = doc?.id || '';
  const docKind = doc?.kind || '';

  useEffect(() => {
    const seq = ++sequence.current;
    setRoute(null);
    setFiles([]);
    setRouteError('');
    setFilesError('');
    if (!docId) {
      setLoadingRoute(false);
      setLoadingFiles(false);
      return;
    }

    setLoadingRoute(true);
    setLoadingFiles(true);

    const entityType = 'document';
    const entityCode = docKind === 'contract' ? 'sed_contracts' : 'sed_letters';

    void loadSedRouteDetails(entityType, entityCode, docId)
      .then((details) => {
        if (seq !== sequence.current) return;
        setRoute(details);
      })
      .catch((cause) => {
        if (seq !== sequence.current) return;
        setRouteError(friendlyError(cause));
      })
      .finally(() => {
        if (seq === sequence.current) setLoadingRoute(false);
      });

    void loadSedFiles(docId)
      .then((items) => {
        if (seq !== sequence.current) return;
        setFiles(items);
      })
      .catch((cause) => {
        if (seq !== sequence.current) return;
        setFilesError(friendlyError(cause));
      })
      .finally(() => {
        if (seq === sequence.current) setLoadingFiles(false);
      });

    return () => {
      sequence.current++;
    };
  }, [docId, docKind]);

  const download = useCallback(async (file: SedFileInfo) => {
    setDownloadingId(file.id);
    try {
      await downloadSedFile(file);
    } catch (cause) {
      useToastStore.getState().showError(friendlyError(cause));
    } finally {
      setDownloadingId('');
    }
  }, []);

  return {
    route,
    files,
    loadingRoute,
    loadingFiles,
    routeError,
    filesError,
    downloadingId,
    download,
  };
}
